import { BooleanInput, useRecordContext } from "react-admin";
import { Employee } from "../providers/employeesProvider";
import { Instructor } from "../providers/instructorsProvider";
import { z } from "zod";
import { schemas } from "../api/server.api";
import { Divider, TextField, Typography } from "@mui/material";
import { Fragment, useState } from "react";
import { FlatAccordion } from "../misc/FlatAccordion";
import { Column } from "../misc/Column";

type Grant = z.output<typeof schemas.UpdateUserGrantsDto.shape.delta.element>;

type GrantOption = {
  grant: Grant;
  label: string;
};

type GrantCategory = {
  title: string;
  grants: GrantOption[];
};

const _grantsAssignableByDrivingSchoolManagers: GrantCategory[] = [
  {
    title: "Kalender & Termine",
    grants: [
      { grant: "Instructor:editAvailabilities", label: "Verfügbarkeiten einstellen" },
      { grant: "DrivingLesson:bookWithoutInvite", label: "Fahrstunden erstellen, ohne Einladung" },
      {
        grant: "DrivingLesson:bookOnBehalfOfInstructor",
        label: "Kann für andere Fahrlehrer Fahrstunden einstellen",
      },
      { grant: "Exam:bookWithoutInvite", label: "Prüfungen erstellen, ohne Einladung" },
      { grant: "viewCalendarEventEditHistory", label: "Bearbeitungsverlauf für Termine einsehen" },
    ],
  },
  {
    title: "Theorie & Prüfungen",
    grants: [
      { grant: "manageTheoryLessons", label: "Theorieunterricht verwalten" },
      { grant: "examCreate", label: "Prüfungen organisieren" },
    ],
  },
  {
    title: "Zahlungen & Rechnungen",
    grants: [
      { grant: "createCashPayment", label: "Barzahlungen erfassen" },
      { grant: "createManualInvoice", label: "Manuelle Rechnungen erstellen" },
      { grant: "createCardReaderPayment", label: "Vor-Ort-Zahlungen mit Kartenleser durchführen" },
      { grant: "retryPayments", label: "Zahlungen wiederholen" },
      { grant: "createExternalBankTransfer", label: "Banküberweisungen erfassen" },
      { grant: "Invoice:refund", label: "Rechnungen zurückerstatten" },
      { grant: "giftCredits:add", label: "Guthaben verschenken" },
      { grant: "PrepaidCredits:refund", label: "Prepaid Guthaben zurückerstatten" },
      { grant: "CalendarEvent:triggerPayment", label: "Kann Termine jederzeit bezahlen lassen" },
    ],
  },
  {
    title: "Dokumente & Formulare",
    grants: [
      { grant: "Student:getFilledOutForm", label: "Anträge drucken" },
      { grant: "SignatureRequest:create", label: "Dokumente erstellen (z.B. ABN, Schaltnachweis)" },
    ],
  },
  {
    title: "Berichte & Auswertung",
    grants: [
      { grant: "viewPerformanceOverview", label: "Zugriff Leistungsübersicht" },
      { grant: "viewDrivingSchoolBusinessIntelligenceData", label: "Zugriff Reports" },
    ],
  },
  {
    title: "Verwaltung",
    grants: [
      { grant: "manageFleet", label: "Fuhrpark verwalten" },
      { grant: "manageUsers", label: "Benutzer verwalten" },
    ],
  },
];

const _grantsAssignableByAdminsOnly: GrantOption[] = [
  {
    grant: "SignatureRequest:deleteSignedAusbildungsnachweis",
    label: "Unterschriebene Ausbildungsnachweise löschen",
  },
  { grant: "viewDATEV", label: "DATEV-Einstellungen und Exportdateien ansehen" },
  { grant: "editDATEVConfig", label: "DATEV-Konfiguration bearbeiten" },
  { grant: "changePrice", label: "Preisvorschläge können überschrieben werden" },
  {
    grant: "changeStatusOfStudentWithOutstandingPayments",
    label: "Kann Status von Fahrschülern mit ausstehenden Zahlungen ändern",
  },
  {
    grant: "drivingSchoolOffboarding",
    label: "Erlaubt Zugriff auf Mein Profil > Fahrschule > Offboarding in der App",
  },
  { grant: "CalendarEvent:historical:delete", label: "Stattgefundene Termine nachträglich löschen" },
  { grant: "DrivingLesson:historical:create", label: "Fahrstunden im Nachhinein dokumentieren" },
  { grant: "Exam:historical:create", label: "Prüfungen im Nachhinein dokumentieren" },
  { grant: "TheoryLesson:historical:create", label: "Theoriestunde im Nachhinein dokumentieren" },
  { grant: "manageBkfCourses", label: "BKF-Kurse verwalten" },
  { grant: "Scheduler:view", label: "Zugriff auf den Terminplaner" },
  { grant: "manageDrivingSchoolAutoForm", label: "Auto-Formulare editieren" },
];

export function InstructorOrEmployeeGrantsInput(props: {
  scope: "drivingSchoolManagerAndAutovioAdmin" | "autovioAdminOnly";
  disabled?: boolean;
}) {
  const { scope, disabled } = props;
  const user = useRecordContext<Instructor | Employee>();
  const [searchTerm, setSearchTerm] = useState("");
  const matchingCategories = _getMatchingCategories(searchTerm);

  return scope === "drivingSchoolManagerAndAutovioAdmin" ? (
    <Column>
      <TextField
        fullWidth
        size="small"
        placeholder="Suche die passenden Treffer"
        inputProps={{ "aria-label": "Berechtigungen durchsuchen" }}
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        sx={{ mb: "12px" }}
      />
      <Column>
        {matchingCategories.length > 0 ? (
          matchingCategories.map((category, index) => (
            <Fragment key={`${category.title}-${searchTerm}`}>
              <FlatAccordion
                title={category.title}
                defaultExpanded={!!searchTerm.trim()}
                sx={{ "& .MuiAccordionSummary-root": { padding: 0 } }}
              >
                {category.grants.map((grantOption) => (
                  <_GrantInput key={grantOption.grant} {...grantOption} disabled={disabled} user={user} />
                ))}
              </FlatAccordion>
              {index < matchingCategories.length - 1 && <Divider />}
            </Fragment>
          ))
        ) : (
          <Typography sx={{ pb: "12px" }}>Keine passenden Berechtigungen gefunden.</Typography>
        )}
      </Column>
    </Column>
  ) : (
    _grantsAssignableByAdminsOnly.map((grantOption) => (
      <_GrantInput key={grantOption.grant} {...grantOption} disabled={disabled} user={user} />
    ))
  );
}

function _getMatchingCategories(searchTerm: string): GrantCategory[] {
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase("de-DE");
  if (!normalizedSearchTerm) {
    return _grantsAssignableByDrivingSchoolManagers;
  }

  return _grantsAssignableByDrivingSchoolManagers.flatMap((category) => {
    if (category.title.toLocaleLowerCase("de-DE").includes(normalizedSearchTerm)) {
      return [category];
    }

    const matchingGrants = category.grants.filter((grant) =>
      grant.label.toLocaleLowerCase("de-DE").includes(normalizedSearchTerm),
    );
    return matchingGrants.length > 0 ? [{ ...category, grants: matchingGrants }] : [];
  });
}

function _GrantInput(props: GrantOption & { disabled?: boolean; user: Instructor | Employee | undefined }) {
  const { grant, label, disabled, user } = props;

  return (
    <BooleanInput
      label={label}
      source={`grants.${grant}`}
      disabled={!user || disabled}
      sx={{
        my: 0,
        "& .MuiFormControlLabel-root": { my: -0.5 },
        "& .MuiSwitch-root": { my: -0.5 },
      }}
      validate={
        grant === "changePrice"
          ? (value) => {
              if (!value && user?.roles.includes("drivingSchoolManager")) {
                return `Fahrschulleitern kann diese Berechtigung nicht entzogen werden.`;
              }
            }
          : undefined
      }
    />
  );
}
